import { SubtractNRI } from 'components/nri-table';
import { NRIVerification } from 'components/nri-table/nri-verification';
import { SlotRollupVerificationStatus } from 'components/nri-table/slot-rollup-verification-status';
import {
  TotalUnitInterestCard,
  UnitInterestTableItem,
} from 'components/total-unit-interest-card';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { TractNriMatrixCard } from 'components/tract-nri-matrix-card';
import styles from './slot-interest-info.module.scss';

const NRI_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 8,
});

interface SlotInterestInfoProps {
  /** Slot-level rollup NRI; `null` when no allocations contribute. */
  slotNri: number | null;
  /** Slot-level rollup WI; `null` when no allocations contribute. */
  slotWi: number | null;
  /**
   * Per-UAT NRI rows under this slot. Drives the rollup verification icons,
   * the calculation breakdown popup, and the matrix popup.
   */
  subtracts: SubtractNRI[];
  /** CRM tract Salesforce ID — feeds the matrix popup. */
  tractId: string;
  /** Slot identifier — well UWI for non-LTD slots, slot ID for LTD slots. */
  slotId: string;
  /** Well UWI when this row is a real well; `null` for LTD slots. */
  wellUwi: string | null;
  /** Well/lease name shown as the popup title. */
  wellName: string | null;
  /** Optional handler invoked when a tract link in the calculation popup is clicked. */
  onTractClick?: (tractId: string, subtractId?: string) => void;
  /**
   * When false and there is exactly one subtract, renders interactive
   * NRIVerification controls instead of the read-only rollup icon.
   * Defaults to true (show rollup).
   */
  hasUats?: boolean;
}

interface SlotItemPair {
  nri: UnitInterestTableItem[];
  wi: UnitInterestTableItem[];
}

/**
 * Single-pass build of the per-UAT row lists for both NRI and WI
 * TotalUnitInterestCard popups.
 *
 * @param {SubtractNRI[]} subtracts - Subtracts under the slot
 * @return {SlotItemPair} Both row lists, keyed by interest type
 */
const buildItemPairs = (subtracts: SubtractNRI[]): SlotItemPair => {
  const nri: UnitInterestTableItem[] = [];
  const wi: UnitInterestTableItem[] = [];
  for (const s of subtracts) {
    const base = {
      subtract: s.crmSubtractId ?? '',
      subtractSalesforceId: s.crmSubtractSalesforceId,
      tractSalesforceId: s.crmTractSalesforceId,
      uat: s.uat ?? '',
      value: s.allocationValue,
      valueUnit: s.allocationValueUnit,
      factor: s.allocationFactor,
      nriCalculationId: s.nriCalculationId,
    };
    nri.push({ ...base, uatInterest: s.uatNri ?? 0 });
    wi.push({ ...base, uatInterest: s.uatWi ?? 0 });
  }
  return { nri, wi };
};

interface InterestRowProps {
  type: 'nri' | 'wi';
  label: string;
  slotValue: number | null;
  subtracts: SubtractNRI[];
  items: UnitInterestTableItem[];
  tractId: string;
  slotId: string;
  wellUwi: string | null;
  wellName: string | null;
  onTractClick?: (tractId: string, subtractId?: string) => void;
  /** When true, renders interactive NRIVerification for a single subtract. */
  showInlineVerification: boolean;
}

/**
 * One Slot NRI or WI row matching the NRI Review table layout:
 * rollup/verification → value → calculation popup → matrix popup.
 *
 * @param {InterestRowProps} props - The component props.
 * @return {JSX.Element} The row element.
 */
const InterestRow = ({
  type,
  label,
  slotValue,
  subtracts,
  items,
  tractId,
  slotId,
  wellUwi,
  wellName,
  onTractClick,
  showInlineVerification,
}: InterestRowProps): JSX.Element => {
  const display = slotValue !== null ? NRI_FORMATTER.format(slotValue) : '--';
  const calculationType = type === 'nri' ? 'NRI' : 'WI';
  const matrixSlotId = wellUwi ? undefined : slotId;
  const matrixWellUwi = wellUwi ?? undefined;

  // Single subtract + no UATs → interactive verification on the slot card
  const renderVerification = () => {
    if (showInlineVerification && subtracts.length === 1) {
      const s = subtracts[0];
      const verification =
        type === 'nri'
          ? s.verifications?.nri
          : s.verifications?.workingInterest;

      if (verification) {
        return (
          <NRIVerification
            nriId={`${s.nriCalculationId}-${type}`}
            disableActions={isNil(slotValue)}
            eventsHref={verification.href.events}
            commentsHref={verification.href.comments}
            initialStatus={verification.status}
            initialCommentsCount={verification.commentsCount}
          />
        );
      }
    }
    return <SlotRollupVerificationStatus subtracts={subtracts} type={type} />;
  };

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{display}</span>
      {subtracts.length > 0 && (
        <TotalUnitInterestCard
          slotName={wellName ?? ''}
          items={items}
          calculatedValue={slotValue ?? 0}
          calculationType={calculationType}
          onTractClick={onTractClick}
        />
      )}
      {tractId && slotId && (
        <TractNriMatrixCard
          tractId={tractId}
          slotId={matrixSlotId}
          wellUwi={matrixWellUwi}
          calculationType={calculationType}
        />
      )}
      {renderVerification()}
    </div>
  );
};

/**
 * Right-side "Interest Info" card on each slot row of the Current Development
 * drill-down. Uses the same components as the NRI Review table:
 * - SlotRollupVerificationStatus or inline NRIVerification
 * - TotalUnitInterestCard (calculation breakdown popup)
 * - TractNriMatrixCard (matrix popup)
 *
 * @param {SlotInterestInfoProps} props - The component props.
 * @return {JSX.Element} The slot interest card.
 */
export const SlotInterestInfo = ({
  slotNri,
  slotWi,
  subtracts,
  tractId,
  slotId,
  wellUwi,
  wellName,
  onTractClick,
  hasUats = true,
}: SlotInterestInfoProps): JSX.Element => {
  const items = useMemo(() => buildItemPairs(subtracts), [subtracts]);
  const showInlineVerification = !hasUats;

  return (
    <div className={styles.container}>
      <span className={styles.title}>Interest Info</span>
      <InterestRow
        type="nri"
        label="Slot NRI:"
        slotValue={slotNri}
        subtracts={subtracts}
        items={items.nri}
        tractId={tractId}
        slotId={slotId}
        wellUwi={wellUwi}
        wellName={wellName}
        onTractClick={onTractClick}
        showInlineVerification={showInlineVerification}
      />
      <InterestRow
        type="wi"
        label="Slot WI:"
        slotValue={slotWi}
        subtracts={subtracts}
        items={items.wi}
        tractId={tractId}
        slotId={slotId}
        wellUwi={wellUwi}
        wellName={wellName}
        onTractClick={onTractClick}
        showInlineVerification={showInlineVerification}
      />
    </div>
  );
};
